import "./PaymentsDetailsView.css";

import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import { alpha, Box, Chip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";

import { formatMoney } from "../../formatters";
import { PREDEFINED_CLAIM_FILTERS } from "../../status";
import useDevMode from "../../utils/devmode";
import { isoToDate } from "../../utils/utils";
import ClaimsTable from "../ClaimsTable";
import Loading from "../Loading";
import PDFViewer from "../PDFViewer";
import { redactText } from "../redact";

function PaymentHeader({
  selectedPayment,
  claims,
}: {
  selectedPayment: PaymentMessage;
  claims: ClaimWithProcedureAndPatientMessage[];
}) {
  const {
    payerPaymentId,
    eopPaymentAmount,
    paymentDate,
    practiceDisplayName,
    paymentType,
    reviewStatus,
    reviewReason,
    adjustmentAmount,
    adjustmentType,
    bankTransactionKey,
  } = selectedPayment;
  const theme = useTheme();
  const devMode = useDevMode();
  const paymentIssues =
    reviewStatus && reviewReason && reviewReason.length > 0
      ? [{ wieldyId: payerPaymentId, reviewReason }]
      : [];
  const claimIssues = claims
    .filter(
      ({ reviewStatus: claimReviewStatus, reviewReason: claimReviewReason }) =>
        claimReviewStatus && claimReviewReason && claimReviewReason.length > 0,
    )
    .map((claim) => ({
      wieldyId: claim.claimId,
      reviewReason: claim.reviewReason || [],
    }));
  const issues = paymentIssues.concat(claimIssues);
  return (
    <>
      {devMode && issues.length > 0 && (
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.error.light, 0.12),
            borderLeft: "4px solid #d32f2f",
            borderRadius: ".5rem",
            padding: "1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "flex-start",
            gap: "1rem",
            color: "#000000",
          }}
        >
          <Box className="validation-alert-content">
            <Typography
              variant="h5"
              className="validation-alert-title"
              sx={{
                marginBottom: "0.25rem",
                display: "flex",
                alignItems: "center",
                gap: "0.2rem",
              }}
            >
              <ErrorIcon
                sx={{ fontSize: "1.5rem", color: theme.palette.error.light }}
              />
              Validation Issues Detected
            </Typography>
            <Box
              component="ul"
              sx={{
                margin: 0,
                paddingLeft: "1.25rem",
                listStyleType: "disc",
              }}
            >
              {issues.map((issue) => (
                <Typography
                  key={issue.wieldyId}
                  component="li"
                  variant="body2"
                  sx={{
                    marginBottom: "0.25rem",
                    color: "#000000",
                  }}
                >
                  {issue.wieldyId}: {issue.reviewReason?.join(", ") || ""}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      )}
      <table>
        <thead>
          <tr>
            <td
              className="paymentHeaderCell"
              style={{ borderLeft: "0px", paddingLeft: "0px" }}
            >
              <Typography className="paymentHeaderTypography">
                <span className="paymentHeaderKey">Practice: </span>
                <span style={{ fontWeight: "600" }}>{practiceDisplayName}</span>
              </Typography>
            </td>
            <td className="paymentHeaderCell">
              <Typography
                className="paymentHeaderTypography"
                variant="subtitle1"
                sx={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <span className="paymentHeaderKey">Payment ID: </span>
                <span style={{ fontWeight: "600" }} data-sr-redact>
                  {redactText(payerPaymentId)}
                </span>
                {devMode && bankTransactionKey && (
                  <Chip
                    icon={<CheckIcon />}
                    label="Reconciled"
                    size="small"
                    variant="outlined"
                    sx={{
                      color: "#2e7d32",
                      borderColor: "#2e7d32",
                      "& .MuiChip-icon": { color: "#2e7d32" },
                    }}
                  />
                )}
              </Typography>
            </td>
            <td className="paymentHeaderCell">
              <Typography
                className="paymentHeaderTypography"
                variant="subtitle1"
              >
                <span>Payment Amount: </span>
                <span style={{ fontWeight: "600" }} data-sr-redact>
                  {formatMoney(eopPaymentAmount)}
                </span>
              </Typography>
            </td>
            <td className="paymentHeaderCell">
              <Typography
                className="paymentHeaderTypography"
                variant="subtitle1"
              >
                <span className="paymentHeaderKey">Payment Date: </span>
                <span style={{ fontWeight: "600" }}>
                  {paymentDate
                    ? isoToDate(paymentDate).toLocaleDateString()
                    : "-"}
                </span>
              </Typography>
            </td>
            {paymentType && (
              <td className="paymentHeaderCell">
                <Typography
                  className="paymentHeaderTypography"
                  variant="subtitle1"
                >
                  <span className="paymentHeaderKey">Payment Type: </span>
                  <span style={{ fontWeight: "600" }}>{paymentType}</span>
                </Typography>
              </td>
            )}
            {adjustmentType && (
              <td className="paymentHeaderCell">
                <Typography
                  className="paymentHeaderTypography"
                  variant="subtitle1"
                >
                  <span className="paymentHeaderKey">
                    Payment Adjustment Type:{" "}
                  </span>
                  <span style={{ fontWeight: "600" }}>{adjustmentType}</span>
                </Typography>
              </td>
            )}
            {adjustmentAmount && (
              <td className="paymentHeaderCell">
                <Typography
                  className="paymentHeaderTypography"
                  variant="subtitle1"
                >
                  <span className="paymentHeaderKey">
                    Payment Adjustment Amount:{" "}
                  </span>
                  <span style={{ fontWeight: "600" }}>
                    {formatMoney(adjustmentAmount)}
                  </span>
                </Typography>
              </td>
            )}
          </tr>
        </thead>
      </table>
    </>
  );
}

interface PaymentsDetailsViewProps {
  claims: ClaimWithProcedureAndPatientMessage[];
  updateClaim: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  postToPMS: (claim: ClaimWithProcedureAndPatientMessage) => void;
  payment: PaymentMessage | null;
  loadingClaims: boolean;
  practices: PracticeMessage[];
  allPractices: PracticeMessage[];
  onSaveProcedure: (procedure: ProcedureMessage) => Promise<void>;
  onDeleteProcedure: (procedure: ProcedureMessage) => Promise<void>;
  onSaveClaim: (claim: ClaimMessage, file: File | null) => Promise<void>;
  onDeleteClaim: (claim: ClaimWithProcedureAndPatientMessage) => Promise<void>;
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
  validateClaim: (claim: ClaimWithProcedureAndPatientMessage) => Promise<void>;
  onDone: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  onDenialUpdate?: (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => Promise<void>;
}

export default function PaymentsDetailsView({
  claims,
  updateClaim,
  onSaveProcedure,
  onDeleteProcedure,
  onSaveClaim,
  onDeleteClaim,
  postToPMS,
  payment,
  loadingClaims,
  practices: _practices,
  allPractices,
  supportedPayers,
  supportedSources,
  validateClaim,
  onDone,
  onDenialUpdate,
}: PaymentsDetailsViewProps) {
  if (!payment) {
    return (
      <div
        style={{
          display: "flex",
          height: "80vh",
        }}
      >
        <Loading />
      </div>
    );
  }

  return (
    <>
      <h1 style={{ marginBottom: "12px" }}>{payment.payer}</h1>
      <PaymentHeader selectedPayment={payment} claims={claims} />
      <Grid
        container
        spacing={2}
        style={{
          marginTop: "27px",
          padding: "0px 2px",
        }}
      >
        <Grid item xs={12}>
          <ClaimsTable
            payment={payment}
            practices={allPractices}
            claims={claims}
            isLoading={loadingClaims}
            onClaimUpdate={updateClaim}
            onPostToPMS={postToPMS}
            enableClaimDetails
            initialSortingModel={[
              { field: "urgency", sort: "desc" },
              { field: "wieldyPatientName", sort: "asc" },
            ]}
            currentTab={PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS}
            sortColumnOrder={[
              "claimStatus",
              "availableSince",
              "practiceDisplayName",
              "payer",
              "wieldyPatientName",
              "claimId",
              "dateOfService",
              "wieldyClaimDate",
              "claimPayerPays",
              "postedState",
              "postedDateTime",
              "denialStatus",
              "action",
              "denialUrgency",
              "doneButton",
              "takeActionButton",
              "daysSinceNotice",
              "daysSinceService",
              "documents",
            ]}
            onSaveClaim={onSaveClaim}
            onDeleteClaim={onDeleteClaim}
            onSaveProcedure={onSaveProcedure}
            onDeleteProcedure={onDeleteProcedure}
            supportedPayers={supportedPayers}
            supportedSources={supportedSources}
            persistKey="paymentDetailsView"
            pagination={{
              page: 1,
              pageSize: 500,
              rowCount: claims.length,
              totalPages: 1,
              onPageChange: () => undefined,
              onPageSizeChange: () => undefined,
            }}
            validateClaim={validateClaim}
            onDone={onDone}
            onDenialUpdate={onDenialUpdate}
          />
        </Grid>
        <Grid item xs={12}>
          <PDFViewer selectedPayment={payment} />
        </Grid>
      </Grid>
    </>
  );
}
